import { useInView } from 'react-intersection-observer';
import { FormattedMessage } from 'react-intl';

import { Button } from '@xing-com/button';
import { EntityPageContractType } from '@xing-com/crate-common-graphql-types';
import type {
  Company,
  EntityPagePublicationStatus,
  Maybe,
} from '@xing-com/crate-common-graphql-types';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useDeviceInfo } from '@xing-com/crate-xinglet';
import { StarRating } from '@xing-com/star-rating';
import { BodyCopy, Meta } from '@xing-com/typography';

import freeCover from '../../assets/svg/free-cover.svg';
import { changeURLtoMobile } from '../../routes/routes';

import * as Styled from './company-card.styles';

const getDraftText: (
  // eslint-disable-next-line no-unused-vars
  publicationStatus: EntityPagePublicationStatus | undefined
) => string = (publicationStatus) => {
  if (publicationStatus === 'DRAFT') {
    return 'CP_DRAFT_COMPANY_WAITING_APPROVAL';
  } else if (publicationStatus === 'CREATED') {
    return 'CP_DRAFT_COMPANY_FLAG_WARNING';
  }

  return '';
};

const isPublished: (
  publicationStatus: EntityPagePublicationStatus | undefined
) => boolean = (publicationStatus) =>
  !publicationStatus ||
  (publicationStatus && publicationStatus === 'PUBLISHED');

type CompanyCardProps = {
  company: Maybe<Company>;
  buttons?: Array<Maybe<JSX.Element>>;
  customContent?: JSX.Element;
  className?: string;
  onClick?: () => void;
  onWasSeen?: () => void;
};
const CompanyCard: React.FC<CompanyCardProps> = ({
  company,
  buttons,
  customContent,
  onClick,
  onWasSeen,
}) => {
  const { isLoggedIn } = useLoginState();
  const { mobileProtocol, isAndroid } = useDeviceInfo();

  const isFreeCompany =
    company?.entityPage?.contract?.type === EntityPageContractType.Free;

  const publicationStatus = company?.entityPage?.publicationStatus;
  const showButtons =
    buttons &&
    buttons.length > 0 &&
    isPublished(company?.entityPage?.publicationStatus);
  const companyURL = company?.links?.public || company?.links?.default;
  const coverImage = !isFreeCompany
    ? company?.entityPage?.coverImage?.[0]?.url
    : undefined;
  const urlWithReleaseProtocol = mobileProtocol
    ? changeURLtoMobile({
        mobileProtocol,
        url: companyURL as string,
        slug: company?.entityPage?.slug as string,
        isAndroid,
      })
    : companyURL;

  const { ref: inViewRef } = useInView({
    onChange: (inView) => {
      if (inView) onWasSeen?.();
    },
    triggerOnce: true,
  });

  return (
    <Styled.Card data-testid={`company-card-${company?.id}`} ref={inViewRef}>
      {publicationStatus && publicationStatus !== 'PUBLISHED' ? (
        <Styled.OverlayLayer>
          <Styled.Flag>
            <FormattedMessage id={getDraftText(publicationStatus)} />
          </Styled.Flag>
        </Styled.OverlayLayer>
      ) : null}
      <Styled.ProfileLink
        href={urlWithReleaseProtocol as $TSFixMe}
        data-testid="link-to-profile"
        onClick={onClick}
      >
        <Styled.CoverImage
          data-testid={`cover-image-card`}
          style={
            coverImage
              ? { backgroundImage: `url(${coverImage}) ` }
              : { backgroundImage: `url(${freeCover}) ` }
          }
        >
          {/* @ts-expect-error FIXME: SC6 */}
          <Styled.Logo
            size="medium"
            profileName={company?.companyName}
            src={company?.logos?.logo128px}
            type="company"
          />
        </Styled.CoverImage>
        <Styled.CompanyName noMargin as="strong">
          {company?.companyName}
        </Styled.CompanyName>
      </Styled.ProfileLink>

      {company?.address?.city ? (
        <BodyCopy data-testid="company-city" noMargin size="medium">
          {company?.address?.city}
        </BodyCopy>
      ) : null}
      {company?.industry?.localizationValue ? (
        <Meta data-testid="company-industry" size="small">
          {company?.industry?.localizationValue}
        </Meta>
      ) : null}
      {company?.kununuData && (
        <Styled.KununuRating
          isLoggedIn={!isLoggedIn}
          data-testid="kununu-section"
        >
          <StarRating
            rating={(company?.kununuData?.ratingAverage as number) ?? null}
          />
          <Meta size="xsmall" noMargin data-testid="kununu-ratings-count">
            <FormattedMessage
              id="CP_KUNUNU_REVIEWS_NUMBER"
              values={{ number: company?.kununuData?.ratingCount || 0 }}
            />
          </Meta>
        </Styled.KununuRating>
      )}
      {customContent}
      {showButtons && (
        <Styled.ButtonPanel>
          {buttons.map((button) => button)}
        </Styled.ButtonPanel>
      )}
      {publicationStatus === 'CREATED' && !mobileProtocol && (
        <Styled.ButtonPanel>
          <Button
            size="small"
            variant="secondary"
            onClick={() => window.open(urlWithReleaseProtocol as string)}
          >
            <FormattedMessage id="CP_DRAFT_COMPANY_BUTTON_FINISH" />
          </Button>
        </Styled.ButtonPanel>
      )}
    </Styled.Card>
  );
};

export { CompanyCard };
